import type { IntlShape } from 'react-intl';
import {
  ARTIST_BIO_LONG_MAX,
  bioLength,
  profileImageSpec,
  type ArtistProfileResponse,
  type SerializedProfileAsset,
} from 'fym-shared/src/music24/artistProfile';
import { profileKindOfItem, type ProfileSendService } from 'fym-shared/src/music24/profileSend';
import { profileSlotOf } from '../profileSlots';
import { failLine, sendAllItemMessages as m, unavailableLine } from './sendAllItemMessages';
import type { SendAllItemView } from './sendAllModel';

/** The brand names of the services (not translated). */
export const SEND_ALL_SERVICE_NAMES: Readonly<Record<ProfileSendService, string>> = {
  spotify: 'Spotify for Artists',
  youtube: 'YouTube',
  facebook: 'Facebook Page',
  deezer: 'Deezer for Creators',
};

/** The short name in a reason line ("The file is too small for YouTube."). */
const SHORT_NAMES: Readonly<Record<ProfileSendService, string>> = {
  spotify: 'Spotify',
  youtube: 'YouTube',
  facebook: 'Facebook',
  deezer: 'Deezer',
};

/** The current version that an image item sends, or null (texts, links, nothing made). Pure. */
export function assetOfItem(
  profile: Pick<ArtistProfileResponse, 'assets'>,
  view: Pick<SendAllItemView, 'item'>
): SerializedProfileAsset | null {
  const kind = profileKindOfItem(view.item);
  return kind === null ? null : profileSlotOf(profile.assets, kind).current;
}

/** The grey line under the name of an item (design c: size and version, or what text goes). */
export function itemSubLine(
  intl: IntlShape,
  view: Pick<SendAllItemView, 'service' | 'item'>,
  profile: Pick<ArtistProfileResponse, 'assets' | 'bio'>
): string {
  const kind = profileKindOfItem(view.item);
  if (kind !== null) {
    const asset = assetOfItem(profile, view);
    const spec = profileImageSpec(kind);
    return asset === null
      ? intl.formatMessage(m.imageSizeNone, { width: spec.width, height: spec.height })
      : intl.formatMessage(m.imageSize, { width: asset.width, height: asset.height, version: asset.version });
  }
  if (view.item === 'facebookLink') {
    return intl.formatMessage(m.linkDeezer);
  }
  switch (view.service) {
    case 'spotify':
      return intl.formatMessage(m.bioLong, { count: bioLength(profile.bio.bioLong), max: ARTIST_BIO_LONG_MAX });
    case 'youtube':
      return intl.formatMessage(m.bioYoutube);
    case 'facebook':
      return intl.formatMessage(m.bioFacebook);
    case 'deezer':
      return intl.formatMessage(m.bioDeezer);
  }
}

/** The red (failed) or grey (cannot go) line of an item, or null. */
export function itemReasonLine(intl: IntlShape, view: SendAllItemView): { text: string; isError: boolean } | null {
  const service = SHORT_NAMES[view.service];
  if (view.failCode !== null) {
    const text = intl.formatMessage(failLine(view.failCode, view.failDetail), {
      service,
      detail: view.failDetail ?? '',
    });
    return { text, isError: !view.readOnly };
  }
  const why = unavailableLine(view.item, view.unavailable, view.fileRefusal);
  return why === null ? null : { text: intl.formatMessage(why, { service }), isError: false };
}
